import { useEffect, useId, useRef, useState, type KeyboardEvent, type ReactNode } from 'react';
import { cn } from '../foundation/cn';
import { useOnClickOutside, useOnEscape } from '../foundation/hooks';

export interface DropdownProps {
  readonly trigger: ReactNode;
  readonly children: ReactNode;
  readonly align?: 'start' | 'end';
  readonly className?: string;
  /**
   * Accessible name for the trigger button. Needed when the visible trigger content is an
   * avatar/icon, or when parts of it are hidden at some breakpoints — without it the accessible
   * name would change with the viewport. Omit when the trigger's own text already names it.
   */
  readonly triggerLabel?: string;
}

// Lightweight menu popover anchored to its trigger. Positioning is CSS-based (no external
// positioning engine). The trigger is a real <button> so it is keyboard-operable natively;
// arrow/Home/End roving focus follows the WAI-ARIA menu-button pattern.
export function Dropdown({ trigger, children, align = 'start', className, triggerLabel }: DropdownProps): JSX.Element {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const menuId = useId();
  // Requested initial focus target once the menu renders (arrow-key opening).
  const initialFocus = useRef<'first' | 'last' | null>(null);

  useOnClickOutside(ref, open, () => setOpen(false));
  useOnEscape(open, () => {
    setOpen(false);
    triggerRef.current?.focus();
  });

  const enabledItems = (): HTMLButtonElement[] =>
    menuRef.current === null
      ? []
      : Array.from(menuRef.current.querySelectorAll<HTMLButtonElement>('[role="menuitem"]:not([disabled])'));

  const focusAt = (index: number): void => {
    const els = enabledItems();
    if (els.length === 0) return;
    els[((index % els.length) + els.length) % els.length]?.focus();
  };

  useEffect(() => {
    if (!open) {
      initialFocus.current = null;
      return;
    }
    const els = enabledItems();
    if (initialFocus.current === 'last') {
      els[els.length - 1]?.focus();
    } else if (initialFocus.current === 'first') {
      els[0]?.focus();
    }
    initialFocus.current = null;
  }, [open]);

  const onTriggerKeyDown = (e: KeyboardEvent<HTMLButtonElement>): void => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      initialFocus.current = 'first';
      setOpen(true);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      initialFocus.current = 'last';
      setOpen(true);
    }
  };

  const onMenuKeyDown = (e: KeyboardEvent<HTMLDivElement>): void => {
    const els = enabledItems();
    const current = els.indexOf(document.activeElement as HTMLButtonElement);
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      focusAt(current + 1);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      focusAt(current - 1);
    } else if (e.key === 'Home') {
      e.preventDefault();
      els[0]?.focus();
    } else if (e.key === 'End') {
      e.preventDefault();
      els[els.length - 1]?.focus();
    } else if (e.key === 'Tab') {
      // Close but let the browser move focus normally — no trapping.
      setOpen(false);
    }
  };

  return (
    <div ref={ref} className="relative inline-block">
      <button
        ref={triggerRef}
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        {...(triggerLabel === undefined ? {} : { 'aria-label': triggerLabel })}
        aria-controls={menuId}
        onClick={() => setOpen((v) => !v)}
        onKeyDown={onTriggerKeyDown}
        className="inline-flex cursor-pointer"
      >
        {trigger}
      </button>
      {open && (
        <div
          id={menuId}
          ref={menuRef}
          role="menu"
          onKeyDown={onMenuKeyDown}
          className={cn(
            'absolute z-dropdown mt-1 min-w-40 animate-fade-in rounded-token border border-border bg-surface p-1 shadow-e2',
            align === 'end' ? 'end-0' : 'start-0',
            className,
          )}
          onClick={() => setOpen(false)}
        >
          {children}
        </div>
      )}
    </div>
  );
}

export interface DropdownItemProps {
  readonly onSelect?: () => void;
  readonly disabled?: boolean;
  readonly children: ReactNode;
}

export function DropdownItem({ onSelect, disabled = false, children }: DropdownItemProps): JSX.Element {
  return (
    <button
      type="button"
      role="menuitem"
      disabled={disabled}
      onClick={onSelect}
      className={cn(
        'flex w-full items-center rounded-sm px-2 py-1.5 text-start text-sm text-fg',
        'hover:bg-surface-muted focus-visible:outline-none focus-visible:bg-surface-muted',
        'disabled:cursor-not-allowed disabled:opacity-60',
      )}
    >
      {children}
    </button>
  );
}
